import api from "@/api";
import deepClone from "@/utils/deep-clone";

const DEFAULT_INTEGRATIONS = {
    saml: {},
    personio: {},
    bamboo: {},
    hibob: {},
    payfit: {},
    rippling: {},
    workday: {},
    scim: {},
    jira: { resource: {} },
};

const state = {
    info: {},
    orgChartData: [],
    invoices: [],
    invoiceQuantity: 0,
    jiraAuthInitUrl: "#",
};

const getters = {
    info(state) {
        return () => deepClone(state?.info ?? {});
    },
    orgChart(state) {
        return () => deepClone(state.orgChartData || []);
    },
    integrations(state) {
        return () => deepClone(state?.info?.integrations ?? DEFAULT_INTEGRATIONS);
    },
    jiraAuthInitUrl(state) {
        return () => state.jiraAuthInitUrl;
    },
    invoices(state) {
        return deepClone(state?.invoices ?? []);
    },
    invoiceQuantity(state) {
        return state?.invoiceQuantity ?? 0;
    },
};

const actions = {
    get({ commit, state }, { payload: { cache } }) {
        if (cache) {
            const info = getters.info(state)();
            if (info && Object.keys(info).length) {
                return Promise.resolve();
            }
        }

        return api.get(`/api/teams/get/details/current`).then(res => {
            const info = res?.data ?? {};
            info.integrations = { ...DEFAULT_INTEGRATIONS, ...info.integrations };

            commit("updateInfo", { info });
        });
    },

    update({ commit, dispatch, state }, { payload: { scope, update } }) {
        return api.post(`/api/teams/update/global`, { scope, update }).then(res => {
            const token = res?.data?.token;

            if (token) {
                dispatch("auth/updateSession", { payload: { token } }, { root: true });
            }

            const scimToken = res?.data?.scimToken;
            if (scimToken) {
                commit("updateInfo", { info: { ...state.info, scimToken } });
                return;
            }

            const stripeClientSecret = res?.data?.stripeClientSecret;
            if (stripeClientSecret) {
                commit("updateInfo", { info: { ...state.info, stripeClientSecret } });
                return;
            }

            const redirect = res?.data?.redirect;
            if (redirect) {
                window.location.href = redirect;
                return;
            }

            return dispatch({ type: "get", payload: { cache: false } });
        });
    },

    getJiraAuthInit({ commit }) {
        return api.get("/api/jira/auth/init").then(res => {
            commit("updateJiraAuthInitUrl", res.data);
        });
    },

    jiraDisable({ commit }) {
        return api.post("/api/jira/disable").then(() => {
            commit("disableJira");
        });
    },

    removeDummyData({ dispatch }) {
        return api.post(`/api/teams/delete/dummydata`).then(res => {
            const token = res?.data?.token;

            if (token) {
                dispatch("auth/updateSession", { payload: { token } }, { root: true });
            }
        });
    },

    getInvoices({ commit }) {
        return api.get("/api/teams/get/invoice/list/default").then(res => {
            commit("updateInvoices", { invoices: res?.data ?? [] });
        });
    },

    getInvoiceQuantity({ commit }) {
        return api.get("/api/teams/get/invoice/quantity").then(res => {
            commit("updateInvoiceQuantity", { quantity: res?.data ?? 0 });
        });
    },

    getOrgChart({ commit }) {
        return api.get(`/api/users/orgchart`).then(res => {
            commit("updateOrgChartData", { data: res?.data ?? [] });
        });
    },
};

const mutations = {
    updateInfo(state, { info }) {
        state.info = { ...info };
    },
    updateInvoices(state, { invoices }) {
        state.invoices = [...invoices];
    },
    updateInvoiceQuantity(state, { quantity }) {
        state.invoiceQuantity = quantity;
    },
    updateJiraAuthInitUrl(state, { url }) {
        state.jiraAuthInitUrl = url;
    },
    disableJira(state) {
        state.info.integrations.jira = DEFAULT_INTEGRATIONS.jira;
    },
    updateOrgChartData(state, { data }) {
        state.orgChartData = data;
    },
};

export default {
    namespaced: true,
    state,
    getters,
    actions,
    mutations,
};

export { DEFAULT_INTEGRATIONS };
